/**
 * projectsSlice.ts — Redux slice for the Projects resource.
 *
 * WEB-340: activeProject now BE-persisted via PATCH /user/active-project
 * (mirror of activeOrg WEB-329). Removed localStorage (projectStorage) as
 * source of truth — BE holds state across reloads.
 *
 * On load: fetchProjectsThunk reads user.activeProjectId from auth state
 * (populated by fetchMeThunk via GET /auth/me) and restores currentProject.
 *
 * switchActiveProjectThunk(projectId): calls PATCH /user/active-project →
 * on success sets currentProject in store. Mirror of switchActiveOrgThunk.
 *
 * WEB-499 (Variant A, ratified): org-mode ("Вся организация") is now
 * BE-persisted too. activeProjectId === null coming back from GET /auth/me
 * means EXPLICIT org-mode, not "no choice made yet" — fetchProjectsThunk.fulfilled
 * no longer falls back to projects[0] when activeProjectId is null (that WEB-340
 * fallback is superseded). clearCurrentProject() (FE-only, no BE call) is kept
 * for reducer-internal reuse, but the three org-mode entry points (ProjectSwitcher,
 * OrgSwitcher, OrgSettingsPanel) now dispatch clearActiveProjectThunk, which calls
 * DELETE /user/active-project so a reload restores org-mode instead of the last
 * project. Best-effort: a failed DELETE still enters org-mode locally (the user
 * must not get stuck because a background persist call failed) and reports the
 * error to Sentry instead of throwing.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { projectsApi, authApi } from '@/api';
import { switchActiveOrgThunk } from './organizationsSlice';
// WEB-1866: срез проектов слушает УСПЕХ передачи владения (см. редьюсер
// внизу). Имя действия и его форма живут в общем файле без зависимостей —
// прямой импорт среза участников затащил бы сюда клиент API (см. докблок
// ownershipTransfer.ts).
import { isTransferOwnershipFulfilled } from './ownershipTransfer';
import type { ProjectsState, Project } from '@/types';
import { extractApiError, type ApiErrorInfo } from '@/api/errorStatus';
import { rejectedApiError } from './rejectedApiError';

const initialState: ProjectsState = {
  projects: [],
  currentProject: null,
  isLoading: false,
  error: null,
  // WEB-438: org-mode intent (false = project-mode). См. ProjectsState JSDoc.
  orgMode: false,
  // WEB-820: загрузка проектов ещё не завершалась.
  loaded: false,
  // Ревизия пачки 37 (С2): загрузка ещё не стартовала — привязки нет.
  orgId: null,
};

/**
 * Организация, под которую стартовал запрос. `undefined` (запрос без
 * организации) и `null` — одно и то же состояние «все мои проекты»,
 * приводим к одному значению, чтобы сравнение было строгим.
 */
function argOrgId(action: { meta?: { arg?: string } }): string | null {
  return action.meta?.arg ?? null;
}

/** Thunk payload shape returned by fetchProjectsThunk. */
interface FetchProjectsResult {
  projects: Project[];
  /** activeProjectId from BE-persisted user profile (may be null). */
  activeProjectId: string | null;
}

/**
 * WEB-1368 п.1.11: на загрузке кабинета список проектов одной организации
 * просили два места разом — стартовый запрос (App.tsx, из ответа /auth/me) и
 * реакция на автопоправку организации (useActiveOrgSync), в любом порядке.
 * Эти два места зовут `fetchProjectsOnLoadThunk`: если запрос той же
 * организации уже в полёте, он ждёт прежний ответ, а не шлёт второй GET.
 * Обычный `fetchProjectsThunk` не присоединяется никогда: его зовут после
 * правок (создан проект, создана организация, принято приглашение), и ответ,
 * отправленный ДО правки, ему не годится. Сам он при этом записывается в
 * полёт — загрузочный запрос за ним присоединится к свежему ответу.
 */
const listsInFlight = new Map<string, ReturnType<typeof projectsApi.list>>();

function listProjects(orgId: string | undefined, joinInFlight: boolean) {
  const key = orgId ?? '';
  const pending = listsInFlight.get(key);
  if (joinInFlight && pending) return pending;
  // Отдаём сам запрос, а не цепочку поверх него: лишний шаг ожидания сдвинул
  // бы окончание загрузки позже соседних действий (вход в созданный проект
  // ждёт, что список доедет раньше switchActiveProjectThunk).
  const request = projectsApi.list(orgId);
  listsInFlight.set(key, request);
  const forget = () => {
    if (listsInFlight.get(key) === request) listsInFlight.delete(key);
  };
  void Promise.resolve(request).then(forget, forget);
  return request;
}

// WEB-329: optional orgId scopes the project list to that organization.
function fetchProjectsWith(joinInFlight: boolean) {
  return createAsyncThunk<
    FetchProjectsResult,
    string | undefined,
    { rejectValue: ApiErrorInfo }
  >(
    'projects/fetchAll',
    async (orgId, { rejectWithValue, getState }) => {
      try {
        const response = await listProjects(orgId, joinInFlight);
        // WEB-518: no more silent auto-create of "My Project" when the list is
        // empty. An empty org now surfaces an explicit empty-state with a
        // "Создать проект" button (ProjectsEmptyBanner) instead of a project
        // being created behind the user's back — this used to conflict with
        // org-mode ("Вся организация") and silently burned the project quota.
        const projects = (response.data as { projects: Project[] }).projects;

        // WEB-340: Read activeProjectId from BE-persisted auth state for restore-on-load.
        // auth.user is populated by fetchMeThunk (App.tsx boot sequence).
        // Use a type cast instead of a state constraint to keep AppDispatch compatibility.
        const state = getState() as { auth?: { user?: { activeProjectId?: string | null } } };
        const activeProjectId = state.auth?.user?.activeProjectId ?? null;

        return { projects, activeProjectId };
      } catch (error: unknown) {
        return rejectWithValue(extractApiError(error));
      }
    }
  );
}

export const fetchProjectsThunk = fetchProjectsWith(false);

/**
 * WEB-1368 п.1.11: тот же запрос списка (те же действия `projects/fetchAll`,
 * те же обработчики слайса), но присоединяется к запросу той же организации в
 * полёте. Только для загрузки кабинета — App.tsx и useActiveOrgSync.
 */
export const fetchProjectsOnLoadThunk = fetchProjectsWith(true);

// WEB-341: Delete project with safeguard payload {confirmName, password}.
// On success removes the project from the store and clears currentProject if
// the deleted project was active.
export const deleteProjectThunk = createAsyncThunk<
  string,
  { id: string; dto: { confirmName: string; password: string } },
  { rejectValue: ApiErrorInfo }
>(
  'projects/delete',
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      await projectsApi.delete(id, dto);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

// WEB-340: BE-persist the active project — mirror of switchActiveOrgThunk.
// Calls PATCH /user/active-project → on success sets currentProject in store.
// On 403 (not a member) the store is NOT updated and an error toast should be shown.
export const switchActiveProjectThunk = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorInfo }
>(
  'projects/switchActive',
  async (projectId, { rejectWithValue }) => {
    try {
      await authApi.setActiveProject(projectId);
      return projectId;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

// WEB-499: BE-persist org-mode entry — mirror of switchActiveProjectThunk but
// best-effort. DELETE /user/active-project clears activeProjectId server-side
// so a reload restores org-mode instead of falling back to the last project.
// Unlike switchActiveProjectThunk this thunk never rejects: a failed background
// persist call must not block the user from entering org-mode this session —
// the failure is reported to Sentry instead.
export const clearActiveProjectThunk = createAsyncThunk<void, void>(
  'projects/clearActive',
  async () => {
    try {
      await authApi.clearActiveProject();
    } catch (error: unknown) {
      Sentry.captureException(error);
    }
  },
);

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    // WEB-340: setCurrentProject is now a plain action (no localStorage).
    // Used internally after wizard project creation.
    // For user-initiated project selection use switchActiveProjectThunk.
    setCurrentProject(state, action: PayloadAction<Project>) {
      // WEB-438: выбор проекта → выходим из org-mode.
      return { ...state, currentProject: action.payload, orgMode: false };
    },
    // WEB-340: FE-only org-mode entry. Sets currentProject to null so Sidebar
    // switches to org-mode. No BE call — the last activeProjectId stays on BE;
    // on reload it will be restored from user.activeProjectId.
    // WEB-438: orgMode=true — намеренный вход в org-mode; fetchProjectsThunk.fulfilled
    // не должен авто-восстанавливать currentProject (иначе avatar-switch откатывал sidebar).
    clearCurrentProject(state) {
      return { ...state, currentProject: null, orgMode: true };
    },
    clearError(state) {
      return { ...state, error: null };
    },
  },
  extraReducers: (builder) => {
    builder
      /**
       * РЕВИЗИЯ ПАЧКИ 37 (С2) — список проектов принадлежит ОРГАНИЗАЦИИ.
       *
       * Раньше ответ писался безусловно: при быстром переключении
       * организаций (A → B → A) опоздавший ответ по B ложился поверх A, и
       * правило п.1.7 ниже («выбранного проекта нет в свежем списке → режим
       * организации») выбрасывало текущий проект, сверившись с ЧУЖИМ
       * списком. Теперь старт запоминает организацию, а ответ и отказ не по
       * ней игнорируются. Сравнение СТРОГОЕ: мягкая форма
       * (`orgId != null && …`) отключала бы сторож в состоянии «привязки
       * нет» — ровно тот дефект, что нашёлся у списка воронок (С1).
       */
      .addCase(fetchProjectsThunk.pending, (state, action) => ({
        ...state,
        isLoading: true,
        error: null,
        orgId: argOrgId(action),
      }))
      .addCase(fetchProjectsThunk.fulfilled, (state, action) => {
        if (state.orgId !== argOrgId(action)) return state;
        const { projects, activeProjectId } = action.payload;
        // WEB-340: Restore from BE-persisted activeProjectId (replaces localStorage).
        // WEB-499 (Variant A): activeProjectId === null now means EXPLICIT
        // org-mode (persisted via DELETE /user/active-project) — no more
        // fallback to projects[0]. A stale activeProjectId (project deleted /
        // no longer in the list) also lands in org-mode rather than an
        // arbitrary project the user has no context on.
        const restored = activeProjectId
          ? projects.find((p) => p.id === activeProjectId) ?? null
          : null;
        // WEB-438: в намеренном org-mode (avatar-switch/toggle) НЕ восстанавливаем
        // проект — иначе sidebar откатывался бы в project-mode после загрузки
        // проектов новой орги. Список проектов при этом всё равно грузится.
        /**
         * WEB-1368 п.1.7 — выбранный проект сверяется со СВЕЖИМ списком.
         *
         * Восстановленный с сервера идентификатор всегда проверялся на
         * присутствие в списке, а уже выбранный проект подставлялся как есть.
         * Из-за этого проект, доступ к которому человек потерял (вывели из
         * участников, проект удалили в соседней вкладке), продолжал висеть в
         * боковом меню до перезагрузки. Утечки данных тут нет — сервер
         * отказывает сам; это чистое неудобство, и лечится тем же приёмом,
         * что уже применён к восстановленному идентификатору.
         */
        const currentStillMine =
          state.currentProject != null &&
          projects.some((p) => p.id === state.currentProject?.id)
            ? state.currentProject
            : null;
        const next = state.orgMode ? null : (currentStillMine ?? restored);
        // WEB-499: no project to land on (fresh boot or stale id) → org-mode is
        // the correct resting state, not a silent auto-pick of projects[0].
        const nextOrgMode = state.orgMode || (!currentStillMine && !restored);
        return {
          ...state,
          isLoading: false,
          loaded: true,
          projects,
          currentProject: next,
          orgMode: nextOrgMode,
        };
      })
      .addCase(fetchProjectsThunk.rejected, (state, action) => {
        if (state.orgId !== argOrgId(action)) return state;
        return {
          ...state,
          isLoading: false,
          // WEB-820: попытка завершилась — пусть и неудачно. Иначе посадочная
          // страница вечно ждала бы загрузку и человек упирался в прелоадер.
          loaded: true,
          error: rejectedApiError(action),
        };
      })
      // WEB-517: org-switch must reset currentProject in the SAME reducer
      // transition as activeOrgId changes. Previously this reset only happened
      // because call sites (OrgSwitcher.tsx, OrgSettingsPanel.tsx) remembered to
      // dispatch clearCurrentProject() right after switchActiveOrgThunk — a
      // stale currentProject from the PREVIOUS org could otherwise leak into
      // project-scoped reads (e.g. TopUp) right after switching, since a forgotten
      // call site would silently keep pointing at the old org's project. Centralizing
      // the reset here makes every org switch, current and future, safe by
      // construction instead of by convention.
      .addCase(switchActiveOrgThunk.fulfilled, (state) => ({
        ...state,
        currentProject: null,
        orgMode: true,
      }))
      // WEB-499: BE-persisted org-mode entry — same reducer transition as the
      // FE-only clearCurrentProject() action (currentProject null + orgMode true).
      .addCase(clearActiveProjectThunk.fulfilled, (state) => ({
        ...state,
        currentProject: null,
        orgMode: true,
      }))
      .addCase(switchActiveProjectThunk.fulfilled, (state, action) => {
        // Find the project in the loaded list and set as currentProject.
        // WEB-438: выбор проекта → выходим из org-mode.
        const project = state.projects.find((p) => p.id === action.payload) ?? null;
        return { ...state, currentProject: project, orgMode: false };
      })
      // WEB-341: Remove deleted project from list; clear currentProject if it was the deleted one.
      /**
       * WEB-1368 п.1.6 — удалили ТЕКУЩИЙ проект, а кабинет оставался в
       * проектном режиме без проекта: пустой заголовок и кнопки без действия
       * до перезагрузки. У соседнего сценария «выйти из проекта»
       * (clearCurrentProject / clearActiveProjectThunk) перевод в режим
       * организации уже сделан — здесь тот же переход, одной дверью меньше.
       */
      .addCase(deleteProjectThunk.fulfilled, (state, action) => {
        const wasCurrent = state.currentProject?.id === action.payload;
        return {
          ...state,
          projects: state.projects.filter((p) => p.id !== action.payload),
          currentProject: wasCurrent ? null : state.currentProject,
          orgMode: wasCurrent ? true : state.orgMode,
        };
      })
      /**
       * WEB-1866 — передал владение: моя роль В СПИСКЕ ПРОЕКТОВ устарела тем
       * же ответом сервера, и её надо поправить сразу.
       *
       * Почему это важно именно здесь: роль из списка проектов — ЗАПАСНОЙ
       * источник роли для всего кабинета (useProjectRole). Пока бандл прав
       * годен, побеждает он, но перечитывание my-role после передачи может
       * отказать (моргнула сеть), а его отказ обнуляет бандл — и роль
       * откатывается сюда, на «владелец». Бывший владелец снова видел
       * «Сделать владельцем», «Удалить проект» и вкладку «Платежи», а сервер
       * отвечал 403 (сам сервер защищён, WEB-1559 — врал только экран).
       *
       * Признак `actorLostOwnership` считает сам thunk по строке участника и
       * роли в организации — здесь его НЕ пересчитываем (иначе получилась бы
       * вторая копия правила, расходящаяся с первой). Сторож `role === 'owner'`
       * оставлен намеренно: понижаем только настоящее владение, повышений и
       * прочих переходов ролей это правило не делает.
       */
      .addMatcher(isTransferOwnershipFulfilled, (state, action) => {
        const { projectId, actorLostOwnership } = action.payload;
        if (!actorLostOwnership) return state;
        const demote = (p: Project): Project =>
          p.id === projectId && p.role === 'owner'
            ? { ...p, role: 'admin' }
            : p;
        return {
          ...state,
          projects: state.projects.map(demote),
          currentProject: state.currentProject
            ? demote(state.currentProject)
            : state.currentProject,
        };
      });
  },
});

export const { setCurrentProject, clearCurrentProject, clearError } = projectsSlice.actions;
export default projectsSlice.reducer;
